import type { QueryClient } from '@tanstack/react-query'
import { wsRoom } from '@apiary/shared'
import { useConnectionStore } from '@/stores/connection'
import { qk } from './query-keys'
import type { IncomingEvent } from './ws-cache'
import { applyServerEvent } from './ws-cache'

/**
 * WS-клиент панели (docs/06 §8).
 *
 * Ключевые решения:
 *  - токен уходит в `auth.token` при handshake, **не** в query-строке
 *    (query попадает в логи nginx — docs/06 §8, docs/09);
 *  - подписка на комнату подтверждается ack'ом; комнаты пересобираются
 *    после реконнекта, потому что сервер про них уже забыл;
 *  - реконнект не сбрасывает данные: помечаем запросы устаревшими
 *    (`invalidateQueries`), а не удаляем их из кэша;
 *  - транспорт абстрагирован (`SocketFactory`), чтобы тесты и mock-режим
 *    подсовывали фальшивый сокет без сети.
 */

export type WsListener = (...args: unknown[]) => void

/** Минимальный контракт транспорта — ровно то, что использует клиент. */
export interface SocketLike {
  readonly connected: boolean
  connect(): void
  disconnect(): void
  on(event: string, listener: WsListener): void
  off(event: string, listener?: WsListener): void
  emit(event: string, ...args: unknown[]): void
}

export type SocketFactory = (token: string) => SocketLike

/** Имена серверных событий, которые клиент кладёт в кэш. */
const CACHE_EVENTS = [
  'bot.state',
  'bot.event',
  'bot.command.ack',
  'bot.deleted',
  'mcAccount.authCode',
  'mcAccount.status',
  'runner.status',
  'dashboard.tick'
] as const satisfies readonly IncomingEvent['type'][]

export interface WsClientOptions {
  queryClient: QueryClient
  factory: SocketFactory
  /** docs/06 §8: батч логов приходит как `bot.event.batch`. */
  batchEvent?: string
  /**
   * Обновление access-токена после отказа handshake. Передаётся снаружи, а не
   * импортируется: клиент не должен тянуть за собой HTTP-слой, а тест — сеть.
   */
  refreshToken?: () => Promise<string>
}

export class WsClient {
  private socket: SocketLike | null = null
  private readonly rooms = new Map<string, number>()
  private readonly pendingSubscriptions = new Map<string, Promise<void>>()
  private hasBeenOnline = false

  constructor(private readonly options: WsClientOptions) {}

  get connected(): boolean {
    return this.socket?.connected === true
  }

  connect(token: string): void {
    if (this.socket !== null) return
    useConnectionStore.getState().setStatus('connecting')
    const socket = this.options.factory(token)
    this.socket = socket
    this.wire(socket)
    socket.connect()
  }

  disconnect(): void {
    this.socket?.disconnect()
    this.socket = null
    this.rooms.clear()
    this.pendingSubscriptions.clear()
    this.hasBeenOnline = false
    useConnectionStore.getState().setStatus('idle')
  }

  /**
   * Подписка с подсчётом ссылок: две карточки одного бота на экране не должны
   * порождать две подписки, а закрытие одной — не должно убивать вторую.
   */
  async subscribe(room: string): Promise<void> {
    const count = this.rooms.get(room) ?? 0
    this.rooms.set(room, count + 1)
    if (count > 0) {
      await this.pendingSubscriptions.get(room)
      return
    }
    await this.emitSubscribe(room)
  }

  release(room: string): void {
    const count = this.rooms.get(room) ?? 0
    if (count <= 1) {
      this.rooms.delete(room)
      this.pendingSubscriptions.delete(room)
      this.socket?.emit('unsubscribe', { room })
      return
    }
    this.rooms.set(room, count - 1)
  }

  /** Список комнат — для отладки и тестов. */
  activeRooms(): string[] {
    return [...this.rooms.keys()]
  }

  /**
   * Подписка на серверное событие, которое **не** идёт в кэш запросов.
   *
   * Так устроены кадры камеры и мини-карты: они бинарные, приходят десятки раз
   * в секунду и живут в WebGL/canvas, а не в TanStack Query. Гнать их через
   * `applyServerEvent` значило бы дёргать ререндер React'а на каждый чанк.
   */
  on(event: string, listener: WsListener): () => void {
    const socket = this.socket
    if (socket === null) return () => undefined
    socket.on(event, listener)
    return () => socket.off(event, listener)
  }

  /** Отправка события серверу (heartbeat камеры, смена viewDistance). */
  send(event: string, payload: unknown): void {
    this.socket?.emit(event, payload)
  }

  private emitSubscribe(room: string): Promise<void> {
    const socket = this.socket
    if (socket === null) return Promise.resolve()

    const promise = new Promise<void>((resolve, reject) => {
      socket.emit('subscribe', { room }, (ack: unknown) => {
        const ok = typeof ack === 'object' && ack !== null && (ack as { ok?: boolean }).ok === true
        if (ok) {
          resolve()
          return
        }
        const error =
          typeof ack === 'object' && ack !== null ? (ack as { error?: string }).error : undefined
        reject(new Error(error ?? `subscribe rejected: ${room}`))
      })
    })
    this.pendingSubscriptions.set(room, promise)
    return promise
  }

  private wire(socket: SocketLike): void {
    socket.on('connect', () => {
      const store = useConnectionStore.getState()
      store.setStatus('online')
      if (this.hasBeenOnline) this.afterReconnect()
      this.hasBeenOnline = true
      // Комнаты живут на стороне сервера — после реконнекта их надо запросить снова.
      for (const room of this.rooms.keys()) {
        void this.emitSubscribe(room).catch(() => {
          // Права могли измениться — молча теряем комнату, UI покажет пустоту.
        })
      }
    })

    socket.on('disconnect', (...args: unknown[]) => {
      const reason = typeof args[0] === 'string' ? args[0] : null
      useConnectionStore.getState().setStatus('reconnecting', reason)
    })

    socket.on('connect_error', (...args: unknown[]) => {
      const first = args[0]
      const message = first instanceof Error ? first.message : null
      useConnectionStore
        .getState()
        .setStatus(this.hasBeenOnline ? 'reconnecting' : 'offline', message)
      // Протухший access-токен: сам по себе он не обновится, а socket.io будет
      // ретраить бесконечно — в шапке навсегда повисало «переподключаемся».
      // Обновляем токен здесь; следующая попытка (фабрика спрашивает токен
      // перед каждой) уйдёт уже с новым.
      if (message === 'UNAUTHORIZED') void this.options.refreshToken?.().catch(() => undefined)
    })

    for (const name of CACHE_EVENTS) {
      socket.on(name, (...args: unknown[]) => {
        this.dispatch(name, args[0])
      })
    }

    // Батч логов: тот же обработчик, только массивом (docs/06 §8).
    socket.on(this.options.batchEvent ?? 'bot.event.batch', (...args: unknown[]) => {
      const payload = args[0]
      if (!Array.isArray(payload)) return
      for (const item of payload) this.dispatch('bot.event', item)
    })
  }

  private dispatch(type: IncomingEvent['type'], payload: unknown): void {
    if (payload === null || typeof payload !== 'object') return
    // Форма payload'а гарантирована контрактом `ServerEvents` из @apiary/shared;
    // рантайм-валидации здесь нет намеренно — иначе новое поле сервера ломало бы UI.
    applyServerEvent(this.options.queryClient, { type, payload } as IncomingEvent)
  }

  /** docs/06 §8: «реконнект клиента → REST-refetch, WS не даёт истории». */
  private afterReconnect(): void {
    const { queryClient } = this.options
    void queryClient.invalidateQueries({ queryKey: qk.bots.root })
    void queryClient.invalidateQueries({ queryKey: qk.dashboard.summary })
    void queryClient.invalidateQueries({ queryKey: qk.mcAccounts.root })
  }
}

// ───────────────────────────── синглтон ─────────────────────────────

let client: WsClient | null = null

export function initWsClient(options: WsClientOptions): WsClient {
  client = new WsClient(options)
  return client
}

export function getWsClient(): WsClient | null {
  return client
}

export { wsRoom }
